// Uređaj za "Isporuka narudžbi v4": veza, zvuk, cijeli ekran, ne gasi ekran, čitač i tastatura, poruka (toast).
// Svi slušaoci i tajmeri se čiste u beforeDestroy (stara stranica ih nije uklanjala: addEventListener vraća undefined).
//
// Očekuje u komponenti: settings, sheet, closeSheet(), onScanned(), next(), advance(), submitId(), tick(), tx().
import userService from "@/services/user";

const READER_IDLE_MS = 1500; // čitač šalje znakove brzo; ako Enter ne stigne, bafer se odbacuje

export default {
  data() {
    return {
      online: typeof navigator === "undefined" ? true : navigator.onLine !== false,
      soundLocked: false,
      fullscreenOn: false,
      toast: "",
      toastOn: false,
    };
  },
  computed: {
    fullscreenSupported() {
      return typeof document !== "undefined" && !!document.fullscreenEnabled;
    },
    wakeSupported() {
      return typeof navigator !== "undefined" && "wakeLock" in navigator;
    },
  },
  created() {
    this._audio = {};
    this._reader = { active: false, buf: "", t: 0 };
    this._swallowEnterUp = false;
    this._wake = null;
    this._toastTimer = null;
    this._tickTimer = null;
    this._handlers = {};
  },
  mounted() {
    const on = (target, type, fn, opts) => {
      target.addEventListener(type, fn, opts);
      this._handlers[type + (opts === true ? "-capture" : "")] = { target, type, fn, opts };
    };
    on(document, "keypress", this.onReaderKey, true);
    on(document, "keydown", this.onKeyDown);
    on(document, "keyup", this.onKeyUp, true);
    on(document, "pointerdown", this.onPointer, true);
    on(document, "visibilitychange", this.onVisibility);
    on(document, "fullscreenchange", this.onFullscreen);
    on(window, "online", this.onOnline);
    on(window, "offline", this.onOffline);
    this._tickTimer = setInterval(() => this.tick(), 250);
    this.applyWake();
  },
  beforeDestroy() {
    Object.values(this._handlers).forEach((h) => h.target.removeEventListener(h.type, h.fn, h.opts));
    this._handlers = {};
    clearInterval(this._tickTimer);
    clearTimeout(this._toastTimer);
    this.releaseWake();
  },
  methods: {
    // ---------- poruka ----------
    say(msg) {
      this.toast = msg;
      this.toastOn = true;
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        this.toastOn = false;
      }, 2200);
    },

    // ---------- veza ----------
    onOnline() {
      this.online = true;
    },
    onOffline() {
      this.online = false;
    },
    async checkConnection() {
      try {
        await userService.getCurrentUser();
        this.online = true;
      } catch (err) {
        // 401 znači da veza postoji, a prijava je istekla
        if (err && err.response) this.online = true;
        else {
          this.online = false;
          this.say(this.tx("toast.stillOffline"));
        }
      }
    },

    // ---------- zvuk ----------
    audioFor(file) {
      if (!this._audio[file]) this._audio[file] = new Audio("./sounds/" + file);
      return this._audio[file];
    },
    playSound(file) {
      if (!file || !this.settings.sound) return;
      try {
        const a = this.audioFor(file);
        a.currentTime = 0;
        const p = a.play();
        if (p && p.catch) {
          p.catch((e) => {
            if (e && e.name === "NotAllowedError") this.soundLocked = true;
          });
        }
      } catch (e) {
        // bez zvuka, ekran radi dalje
      }
    },
    // kratak ton za greške veze i sesije (nemaju svoj zvuk)
    beep() {
      if (!this.settings.sound) return;
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        this._ctx = this._ctx || new Ctx();
        const ctx = this._ctx;
        if (ctx.state === "suspended") {
          const r = ctx.resume();
          if (r && r.catch) r.catch(() => {});
        }
        if (ctx.state !== "running") {
          this.soundLocked = true;
          return;
        }
        const t0 = ctx.currentTime;
        [0, 0.22].forEach((d) => {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = "square";
          o.frequency.value = 330;
          g.gain.setValueAtTime(0.0001, t0 + d);
          g.gain.exponentialRampToValueAtTime(0.18, t0 + d + 0.02);
          g.gain.exponentialRampToValueAtTime(0.0001, t0 + d + 0.16);
          o.connect(g);
          g.connect(ctx.destination);
          o.start(t0 + d);
          o.stop(t0 + d + 0.18);
        });
      } catch (e) {
        // bez tona
      }
    },
    playForTicket(ticket) {
      if (ticket.kind === "error" || ticket.kind === "session") this.beep();
      else this.playSound(ticket.outcome.sound);
    },
    unlockSound() {
      this.soundLocked = false;
      if (this._ctx && this._ctx.state === "suspended") this._ctx.resume().catch(() => {});
    },
    // svaki dodir otključava zvuk (pravilo preglednika)
    onPointer() {
      if (this.soundLocked) this.unlockSound();
    },

    // ---------- cijeli ekran, ne gasi ekran ----------
    toggleFullscreen() {
      try {
        if (document.fullscreenElement) document.exitFullscreen();
        else document.documentElement.requestFullscreen();
      } catch (e) {
        // preglednik ne dozvoljava
      }
    },
    onFullscreen() {
      this.fullscreenOn = !!document.fullscreenElement;
    },
    async applyWake() {
      if (!this.wakeSupported || !this.settings.awake || this._wake || document.visibilityState !== "visible") return;
      try {
        this._wake = await navigator.wakeLock.request("screen");
        this._wake.addEventListener("release", () => {
          this._wake = null;
        });
      } catch (e) {
        this._wake = null;
      }
    },
    releaseWake() {
      if (this._wake) {
        this._wake.release().catch(() => {});
        this._wake = null;
      }
    },
    onVisibility() {
      if (document.visibilityState === "visible") this.applyWake();
    },

    // ---------- tastatura i čitač ----------
    // Čitač koji tipka šalje {"id":1001,"secure":"…"} + Enter: počinje sa "{", pa se znakovi skupljaju do Entera
    // (isto kao stara stranica). Znakovi se ne puštaju u polje ID u fokusu.
    onReaderKey(e) {
      const r = this._reader;
      const now = Date.now();
      if (r.active && now - r.t > READER_IDLE_MS) r.active = false;
      if (!r.active && e.key === "{") {
        this._reader = { active: true, buf: "", t: now };
      }
      const rd = this._reader;
      if (!rd.active) return;
      rd.t = now;
      e.preventDefault();
      e.stopPropagation();
      if (e.key === "Enter") {
        const raw = rd.buf;
        this._reader = { active: false, buf: "", t: 0 };
        this._swallowEnterUp = true; // keyup Entera ne smije poslati i polje ID
        this.onScanned(raw, "reader");
      } else if (e.key && e.key.length === 1) {
        rd.buf += e.key;
        if (rd.buf.length > 600) this._reader = { active: false, buf: "", t: 0 };
      }
    },
    onKeyUp(e) {
      if (e.key === "Enter" && this._swallowEnterUp) {
        this._swallowEnterUp = false;
        e.stopPropagation();
      }
    },
    onKeyDown(e) {
      if (this._reader.active) return;
      const tag = e.target && e.target.tagName;
      const inInput = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
      if (e.key === "Escape") {
        if (this.sheet) this.closeSheet();
        else if (this.scanInput) {
          this.scanInput = "";
          this.inputBad = "";
        }
        return;
      }
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if ((e.key === "+" || e.code === "NumpadAdd") && this.settings.mode === "manual") {
        e.preventDefault();
        this.next();
      } else if (e.key === "ArrowRight" && !inInput && !this.sheet) {
        e.preventDefault();
        this.advance();
      } else if (e.key === "/" && !inInput && !this.sheet) {
        e.preventDefault();
        this.focusId();
      } else if (/^\d$/.test(e.key) && !inInput && !this.sheet) {
        // cifre bez fokusa u polju (numpad, čitač koji šalje samo broj)
        e.preventDefault();
        this.onIdInput(this.scanInput + e.key);
        this.focusId(true);
      } else if (e.key === "Enter" && !inInput && !this.sheet && this.scanInput && !(e.target.closest && e.target.closest("button"))) {
        this.submitId();
      }
    },
    focusId(end) {
      const f = this.$refs.idfield;
      if (f && f.focus) f.focus(end);
    },
    onIdEnter() {
      this.submitId();
    },
  },
  watch: {
    "settings.awake"(on) {
      if (on) this.applyWake();
      else this.releaseWake();
    },
  },
};
